import { App, Form, Input } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomInput,
  CustomModal,
  CustomSelect,
  Loader,
} from "sipli-fleet-core/components";
import {
  useMutationCreateTireModel,
  useMutationUpdateTireModel,
  useQueryGetTireModelById,
} from "src/queries/administrator/tireModel.queries";
import { useQueryGetTireMakeLookup } from "../../../queries/lookup.queries";
import { TireModelRecord } from "../../../services/administrator/tireModelService";

interface TireModelFormModalsProps {
  open: boolean;
  record?: TireModelRecord | null;
  onClose: () => void;
}

interface TireModelFormValues {
  name: string;
  active: string;
  description?: string;
  tireMake?: string | number;
}

type SelectValue = string | number;

type TireMake = {
  id: SelectValue;
  name: string;
};

type TireMakeLookupPayload = {
  data?: {
    response?: TireMake[];
  };
};

type TireMakeOption = {
  label: string;
  value: SelectValue;
};

type TireModelDetailRecord = Partial<TireModelRecord> & {
  tireMake?: {
    id?: SelectValue;
    name?: string | null;
  } | null;
  idMake?: SelectValue;
};

const getDetailRecord = (payload?: unknown): Partial<TireModelRecord> => {
  const response = payload as
    | TireModelRecord
    | { tireModel?: TireModelRecord }
    | { data?: TireModelRecord }
    | undefined;

  if (!response) return {};

  if ("tireModel" in response && response.tireModel) {
    return response.tireModel;
  }

  if ("data" in response && response.data) {
    return response.data;
  }

  return response as TireModelRecord;
};

const isActive = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  if (typeof value === "string") {
    return ["active", "true", "1"].includes(value.toLowerCase());
  }

  return false;
};

const getTireMakeSelectValue = (
  detailRecord: Partial<TireModelRecord>
): SelectValue | undefined => {
  const record = detailRecord as TireModelDetailRecord;

  if (record.idMake !== undefined && record.idMake !== null) {
    return record.idMake;
  }

  if (record.tireMake?.id !== undefined && record.tireMake?.id !== null) {
    return record.tireMake.id;
  }

  return undefined;
};

const TireModelFormModal = ({ open, record, onClose }: TireModelFormModalsProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<TireModelFormValues>();

  const isEditing = Boolean(record?.id);
  const editId = record?.id;

  const detailQuery = useQueryGetTireModelById(editId, {
    enabled: open && isEditing,
  });

  const createMutation = useMutationCreateTireModel();
  const updateMutation = useMutationUpdateTireModel();

  const detailRecord = useMemo(
    () => getDetailRecord(detailQuery.data?.data?.response),
    [detailQuery.data?.data?.response]
  );

  const { data: tireMakeLookupData, isLoading: isTireMakeLookupLoading } =
    useQueryGetTireMakeLookup({}, { enabled: open });

  const tireMakeOptions = useMemo<TireMakeOption[]>(
    () => {
      const lookupPayload = tireMakeLookupData as TireMakeLookupPayload | undefined;

      return lookupPayload?.data?.response?.map((item: TireMake) => ({
        label: item.name,
        value: item.id,
      })) ?? [];
    },
    [tireMakeLookupData]
  );

  const statusOptions = useMemo(
    () => [
      { label: t("active"), value: "true" },
      { label: t("inactive"), value: "false" },
    ],
    [t]
  );

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue({
      name: "",
      active: "true",
      description: "",
      tireMake: undefined,
    });
  }, [form, open]);

  useEffect(() => {
    if (!open || !isEditing || !detailRecord.id) return;

    form.setFieldsValue({
      name: detailRecord.name ?? "",
      active: String(isActive(detailRecord.active)),
      description: detailRecord.description ?? "",
      tireMake: getTireMakeSelectValue(detailRecord),
    });
  }, [detailRecord, form, isEditing, open]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();

    const payload = {
      active: values.active === "true",
      description: values.description?.trim() ?? "",
      name: values.name.trim(),
      idMake: values.tireMake,
    };

    try {
      if (isEditing && editId) {
        await updateMutation.mutateAsync({
          id: editId,
          payload: {
            id: editId,
            ...payload,
          },
        });

        message.success(t("tire_model_updated"));
      } else {
        await createMutation.mutateAsync(payload);

        message.success(t("tire_model_created"));
      }

      handleClose();
    } catch {
      message.error(t("setup_save_failed"));
    }
  };

  return (
    <CustomModal
      open={open}
      title={t(isEditing ? "edit_tire_model" : "add_tire_model")}
      onCancel={handleClose}
      destroyOnHidden
      width={560}
      footer={null}
    >
      {detailQuery.isLoading ? (
        <Loader height="h-[310px]" />
      ) : (
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          className="grid grid-cols-1 pt-4 gap-x-4 sm:grid-cols-2"
        >
          <Form.Item
            name="name"
            label={t("name")}
            rules={[
              { required: true, whitespace: true, message: t("name_required") },
              { max: 64, message: t("name_max_length", { count: 64 }) },
            ]}
            className="mb-3!"
          >
            <CustomInput placeholder={t("enter_name")} maxLength={64} />
          </Form.Item>

          <Form.Item
            name="tireMake"
            label={t("tire_make")}
            rules={[{ required: true, message: t("tire_make_required") }]}
            className="mb-3!"
          >
            <CustomSelect
              options={tireMakeOptions}
              placeholder={t("select")}
              loading={isTireMakeLookupLoading}
            />
          </Form.Item>

          <Form.Item
            name="active"
            label={t("status")}
            rules={[{ required: true, message: t("status_required") }]}
            className="sm:col-span-2 mb-3!"
          >
            <CustomSelect options={statusOptions} placeholder={t("select")} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t("description")}
            rules={[
              {
                max: 255,
                message: t("description_too_long"),
              },
            ]}
            className="sm:col-span-2 mb-3!"
          >
            <Input.TextArea
              rows={4}
              placeholder={t("enter_description")}
              maxLength={255}
              className="px-2 py-4 custom-input no-resize"
            />
          </Form.Item>

          <Form.Item className="sm:col-span-2 mb-0!">
            <CustomActionButtons
              onCancel={handleClose}
              onOk={handleSubmit}
              loading={createMutation.isPending || updateMutation.isPending}
              okProps={{ htmlType: "button" }}
              cancelBtnLabel="cancel"
              okBtnLabel={isEditing ? "update" : "add"}
            />
          </Form.Item>
        </Form>
      )}
    </CustomModal>
  );
};

export default TireModelFormModal;
